import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from './data/supabaseClient';
import { useTranslation } from 'react-i18next';
import Spinner from './common/Spinner';
import { CustomButton8 } from './Templates';
import './UnsubscribePage.css';

/**
 * UnsubscribePage Component
 *
 * One-click unsubscribe page for newsletter/marketing emails
 * - No login required (uses unsubscribe_token)
 * - Updates newsletter_enabled to false
 * - Shows success/error message
 * - Provides link to settings for re-subscription
 */
const UnsubscribePage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const [status, setStatus] = useState('loading'); // 'loading', 'success', 'error', 'already_unsubscribed'
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const unsubscribe = async () => {
      if (!token) {
        setStatus('error');
        setErrorMessage(t('unsubscribe.invalid_token'));
        return;
      }

      try {
        // First, check if the token exists and get current newsletter status
        const { data: profileData, error: fetchError } = await supabase
          .from('profiles')
          .select('id, newsletter_enabled, email')
          .eq('unsubscribe_token', token)
          .single();

        if (fetchError || !profileData) {
          console.error('Error fetching profile:', fetchError);
          setStatus('error');
          setErrorMessage(t('unsubscribe.token_not_found'));
          return;
        }

        // Check if already unsubscribed
        if (profileData.newsletter_enabled === false) {
          setStatus('already_unsubscribed');
          return;
        }

        // Update newsletter_enabled to false
        const { error: updateError } = await supabase
          .from('profiles')
          .update({
            newsletter_enabled: false,
            updated_at: new Date().toISOString()
          })
          .eq('unsubscribe_token', token);

        if (updateError) {
          console.error('Error updating profile:', updateError);
          setStatus('error');
          setErrorMessage(t('unsubscribe.update_failed'));
          return;
        }

        setStatus('success');
      } catch (err) {
        console.error('Unsubscribe error:', err);
        setStatus('error');
        setErrorMessage(err.message || t('unsubscribe.unknown_error'));
      }
    };

    unsubscribe();
  }, [token, t]);

  const handleGoToSettings = () => {
    navigate('/settings');
  };

  const handleGoHome = () => {
    navigate('/');
  };

  return (
    <div className="usp-unsubscribe-page">
      <div className="usp-unsubscribe-container">
        <div className="usp-unsubscribe-logo">
          <h1 className="usp-logo-text">
            Way<span className="usp-logo-share">Share</span>
          </h1>
        </div>

        {status === 'loading' && (
          <div className="usp-unsubscribe-status">
            <Spinner size="medium" />
            <h2>{t('unsubscribe.processing')}</h2>
            <p>{t('unsubscribe.processing_message')}</p>
          </div>
        )}

        {status === 'success' && (
          <div className="usp-unsubscribe-status success">
            <div className="usp-status-icon usp-success-icon">✓</div>
            <h2>{t('unsubscribe.success_title')}</h2>
            <p className="usp-status-message">{t('unsubscribe.success_message')}</p>
            <p className="usp-status-submessage">{t('unsubscribe.success_submessage')}</p>

            <div className="usp-info-box">
              <h3>📧 {t('unsubscribe.what_happens_title')}</h3>
              <ul>
                <li>✅ {t('unsubscribe.what_happens_1')}</li>
                <li>✅ {t('unsubscribe.what_happens_2')}</li>
                <li>⚠️ {t('unsubscribe.what_happens_3')}</li>
              </ul>
            </div>

            <div className="usp-resubscribe-info">
              <p>{t('unsubscribe.resubscribe_info')}</p>
            </div>

            <div className="usp-action-buttons">
              <CustomButton8 onClick={handleGoToSettings}>
                {t('unsubscribe.go_to_settings')}
              </CustomButton8>
              <CustomButton8 onClick={handleGoHome} style={{ marginLeft: '10px' }}>
                {t('unsubscribe.go_home')}
              </CustomButton8>
            </div>
          </div>
        )}

        {status === 'already_unsubscribed' && (
          <div className="usp-unsubscribe-status already">
            <div className="usp-status-icon usp-info-icon">ℹ</div>
            <h2>{t('unsubscribe.already_unsubscribed_title')}</h2>
            <p className="usp-status-message">{t('unsubscribe.already_unsubscribed_message')}</p>

            <div className="usp-action-buttons">
              <CustomButton8 onClick={handleGoToSettings}>
                {t('unsubscribe.go_to_settings')}
              </CustomButton8>
              <CustomButton8 onClick={handleGoHome} style={{ marginLeft: '10px' }}>
                {t('unsubscribe.go_home')}
              </CustomButton8>
            </div>
          </div>
        )}

        {status === 'error' && (
          <div className="usp-unsubscribe-status error">
            <div className="usp-status-icon usp-error-icon">✕</div>
            <h2>{t('unsubscribe.error_title')}</h2>
            <p className="usp-status-message">{t('unsubscribe.error_message')}</p>
            {errorMessage && (
              <p className="usp-error-details">{errorMessage}</p>
            )}

            <div className="usp-help-box">
              <h3>{t('unsubscribe.need_help')}</h3>
              <p>{t('unsubscribe.contact_support')}</p>
              <a href="mailto:info@wayshare.app">info@wayshare.app</a>
            </div>

            <div className="usp-action-buttons">
              <CustomButton8 onClick={handleGoHome}>
                {t('unsubscribe.go_home')}
              </CustomButton8>
            </div>
          </div>
        )}

        <div className="usp-unsubscribe-footer">
          <p>{t('unsubscribe.footer_text')}</p>
          <p className="usp-footer-links">
            <a href="/privacy">{t('footer.privacy')}</a>
            {' | '}
            <a href="/terms">{t('footer.terms')}</a>
            {' | '}
            <a href="/contact">{t('footer.contact')}</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default UnsubscribePage;
